import {
  SelectableInputFreeInputResolver,
  SelectableInputOption,
  SelectableInputResolveContext,
} from './selectable-input.types';

export type SelectableInputCompareWith = string | ((o1: unknown, o2: unknown) => boolean);

export interface SelectableInputResolvedCandidate {
  option: SelectableInputOption;
  isNew: boolean;
}

export function selectableInputValuesEqual(
  left: unknown,
  right: unknown,
  compareWith?: SelectableInputCompareWith,
): boolean {
  if (typeof compareWith === 'function') return compareWith(left, right);
  if (typeof compareWith === 'string' && left && right && typeof left === 'object' && typeof right === 'object') {
    return (left as Record<string, unknown>)[compareWith] === (right as Record<string, unknown>)[compareWith];
  }
  return left === right;
}

export function findSelectableInputOption(
  options: readonly SelectableInputOption[],
  inputValue: string,
  compareWith?: SelectableInputCompareWith,
): SelectableInputOption | undefined {
  const trimmed = inputValue.trim();
  return options.find(option => (
    option.label.trim() === trimmed
    || selectableInputValuesEqual(option.value, trimmed, compareWith)
  ));
}

export function findEquivalentSelectableInputOption(
  options: readonly SelectableInputOption[],
  candidate: SelectableInputOption,
  compareWith?: SelectableInputCompareWith,
): SelectableInputOption | undefined {
  return options.find(option => (
    selectableInputValuesEqual(option.value, candidate.value, compareWith)
    || option.label.trim() === candidate.label.trim()
  ));
}

export function mergeSelectableInputOptions(
  parentOptions: readonly SelectableInputOption[],
  resolvedOptions: readonly SelectableInputOption[],
  compareWith?: SelectableInputCompareWith,
): SelectableInputOption[] {
  const merged: SelectableInputOption[] = [];
  for (const option of [...parentOptions, ...resolvedOptions]) {
    if (!findEquivalentSelectableInputOption(merged, option, compareWith)) merged.push(option);
  }
  return merged;
}

export function appendSelectableInputValue(
  selectValue: unknown,
  value: unknown,
  compareWith?: SelectableInputCompareWith,
): unknown[] {
  const values = Array.isArray(selectValue) ? [...selectValue] : [];
  if (!values.some(item => selectableInputValuesEqual(item, value, compareWith))) values.push(value);
  return values;
}

export async function resolveSelectableInputCandidate(
  inputValue: string,
  context: SelectableInputResolveContext,
  resolver: SelectableInputFreeInputResolver,
  compareWith?: SelectableInputCompareWith,
): Promise<SelectableInputResolvedCandidate | null> {
  const existing = findSelectableInputOption(context.options, inputValue, compareWith);
  if (existing) return { option: existing, isNew: false };

  const resolved = await resolver(inputValue.trim(), context);
  if (!resolved) return null;
  if (resolved.value === undefined || typeof resolved.label !== 'string' || !resolved.label.trim()) {
    throw new TypeError('resolveFreeInput returned an invalid SelectableInputOption.');
  }

  const equivalent = findEquivalentSelectableInputOption(context.options, resolved, compareWith);
  return equivalent
    ? { option: equivalent, isNew: false }
    : { option: { ...resolved, label: resolved.label.trim() }, isNew: true };
}
